import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Spinner } from "flowbite-react";
import { HiExclamationCircle } from "react-icons/hi";
import { useSignalPlaybooks } from "../../../hooks";
import { SignalList } from "./SignalList";
import { PlaybookEditor } from "./PlaybookEditor";
import {
  groupPlaybooks,
  toLocalItem,
  toPayloadItem,
  actionsEqual,
} from "./playbookUtils";

/**
 * Two-pane master–detail Signals & Playbooks settings surface, embedded
 * directly in the Settings tab (no artboard chrome — the tab already
 * provides title + frame; the parent SettingsPage tab list is already
 * flag-gated, so this component does NOT re-wrap in <FeatureFlag>).
 *
 * Draft state machine (S2): a signal's draft is seeded lazily from the
 * resolved playbook on FIRST edit, preserved across signal selection, and
 * dropped/re-seeded only after a successful Save or Reset. It is NEVER
 * re-synced from playbook.actions on every render (that was the MVP bug —
 * it silently discarded in-progress edits). Toggling is_enabled returns the
 * whole playbook object; because we don't auto-resync, an existing draft
 * naturally overlays the refreshed playbook rather than being dropped.
 */
export const SignalsPlaybooksSettings = () => {
  const { t } = useTranslation();
  const { playbooks, loading, error, refetch, updatePlaybook, resetPlaybook } =
    useSignalPlaybooks();

  const [selectedSignalType, setSelectedSignalType] = useState(null);
  const [filter, setFilter] = useState("all");
  const [drafts, setDrafts] = useState({});
  const [saveStates, setSaveStates] = useState({});
  // Per-signal in-flight toggle guard: prevents a second PATCH while the first
  // is outstanding (double-click / rapid toggle). The switch reflects server
  // truth (playbook.is_enabled), so there is no optimistic state to revert.
  const [togglingSignals, setTogglingSignals] = useState(() => new Set());

  // Default selection: first signal of the first category, once data loads.
  // Filter × selection (S6): selection is never reassigned by filter changes
  // below — only this initial-load effect sets it.
  useEffect(() => {
    if (selectedSignalType || playbooks.length === 0) return;
    const groups = groupPlaybooks(playbooks);
    const first = groups[0]?.items?.[0];
    if (first) setSelectedSignalType(first.signal_type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playbooks]);

  if (loading && playbooks.length === 0) {
    return (
      <div className="flex h-[32rem] w-full items-center justify-center">
        <Spinner
          aria-label={t("pages.settings.signalsPlaybooks.loading")}
          size="xl"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-8">
        <Alert color="failure" icon={HiExclamationCircle}>
          <span>{error}</span>
        </Alert>
        <button
          type="button"
          onClick={refetch}
          className="rounded-lg border border-blue-600 bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  const findPlaybook = (signalType) =>
    playbooks.find((p) => p.signal_type === signalType);

  const seedActions = (signalType) =>
    (findPlaybook(signalType)?.actions || []).map((a) => toLocalItem(a));

  const mutateDraft = (signalType, mutator) => {
    setDrafts((prev) => {
      const base = prev[signalType]
        ? prev[signalType].map((a) => ({ ...a }))
        : seedActions(signalType);
      return { ...prev, [signalType]: mutator(base) };
    });
  };

  const dropDraft = (signalType) => {
    setDrafts((prev) => {
      if (!(signalType in prev)) return prev;
      const next = { ...prev };
      delete next[signalType];
      return next;
    });
  };

  const isDirty = (signalType) => {
    const draft = drafts[signalType];
    if (!draft) return false;
    const playbook = findPlaybook(signalType);
    return !actionsEqual(draft, playbook?.actions || []);
  };

  const selectedPlaybook = findPlaybook(selectedSignalType);
  const displayActions = selectedSignalType
    ? drafts[selectedSignalType] || seedActions(selectedSignalType)
    : [];
  const selectedDirty = selectedSignalType
    ? isDirty(selectedSignalType)
    : false;
  const selectedSaveState = saveStates[selectedSignalType] || "idle";
  const selectedToggling = selectedSignalType
    ? togglingSignals.has(selectedSignalType)
    : false;

  const handleToggle = async (isEnabled) => {
    const signalType = selectedSignalType;
    if (!signalType || togglingSignals.has(signalType)) return;
    setTogglingSignals((prev) => new Set(prev).add(signalType));
    try {
      await updatePlaybook(signalType, { isEnabled });
    } catch (err) {
      // The hook already toasts + rethrows; the switch reflects server truth
      // (playbook.is_enabled), unchanged on failure, so there is nothing to
      // revert. Swallow here only to keep the guard's finally reachable.
    } finally {
      setTogglingSignals((prev) => {
        const next = new Set(prev);
        next.delete(signalType);
        return next;
      });
    }
  };

  const handleTextChange = (index, text) =>
    mutateDraft(selectedSignalType, (actions) => {
      const next = [...actions];
      next[index] = { ...next[index], text };
      return next;
    });

  const handlePriorityChange = (index, priority) =>
    mutateDraft(selectedSignalType, (actions) => {
      const next = [...actions];
      next[index] = { ...next[index], priority };
      return next;
    });

  const handleAdd = () =>
    mutateDraft(selectedSignalType, (actions) => [
      ...actions,
      toLocalItem({ text: "", priority: "medium" }),
    ]);

  const handleRemove = (index) =>
    mutateDraft(selectedSignalType, (actions) =>
      actions.filter((_, i) => i !== index),
    );

  const handleReorder = (fromIndex, toIndex) =>
    mutateDraft(selectedSignalType, (actions) => {
      const next = [...actions];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });

  const handleAutoRank = () =>
    mutateDraft(selectedSignalType, (actions) => {
      const rank = { high: 0, medium: 1, low: 2 };
      return [...actions].sort((a, b) => rank[a.priority] - rank[b.priority]);
    });

  const handleSave = async () => {
    const signalType = selectedSignalType;
    const draft = drafts[signalType];
    if (!draft) return;
    setSaveStates((prev) => ({ ...prev, [signalType]: "saving" }));
    try {
      await updatePlaybook(signalType, { actions: draft.map(toPayloadItem) });
      dropDraft(signalType);
      setSaveStates((prev) => ({ ...prev, [signalType]: "saved" }));
      window.setTimeout(() => {
        setSaveStates((prev) =>
          prev[signalType] === "saved"
            ? { ...prev, [signalType]: "idle" }
            : prev,
        );
      }, 1600);
    } catch (err) {
      setSaveStates((prev) => ({ ...prev, [signalType]: "error" }));
    }
  };

  const handleReset = async () => {
    const signalType = selectedSignalType;
    try {
      await resetPlaybook(signalType);
      dropDraft(signalType);
      setSaveStates((prev) => ({ ...prev, [signalType]: "idle" }));
    } catch (err) {
      // error already surfaced via toast by the hook
    }
  };

  const handleDiscard = () => {
    const signalType = selectedSignalType;
    dropDraft(signalType);
    setSaveStates((prev) => ({ ...prev, [signalType]: "idle" }));
  };

  return (
    <div className="animation-slide-up-lil-bounce flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-sm lg:h-[680px] lg:flex-row">
      <SignalList
        playbooks={playbooks}
        filter={filter}
        selectedSignalType={selectedSignalType}
        isDirty={isDirty}
        onFilterChange={setFilter}
        onSelect={setSelectedSignalType}
      />
      <PlaybookEditor
        playbook={selectedPlaybook}
        actions={displayActions}
        isDirty={selectedDirty}
        saveState={selectedSaveState}
        toggleDisabled={selectedToggling}
        onToggle={handleToggle}
        onTextChange={handleTextChange}
        onPriorityChange={handlePriorityChange}
        onAdd={handleAdd}
        onRemove={handleRemove}
        onReorder={handleReorder}
        onAutoRank={handleAutoRank}
        onSave={handleSave}
        onReset={handleReset}
        onDiscard={handleDiscard}
      />
    </div>
  );
};
